<template>
  <div>
    <div v-if="!loading && !isFinancesEmpty" class="mb-3">
      <v-app>
        <div class="d-flex justify-end">
          <business-guide-search v-model="searchValue" />
        </div>
      </v-app>
    </div>

    <ib-list-view-placeholder
      v-if="!loading && isFinancesEmpty"
      :title="$t('addFinancingOption')"
      :disabled="!canEdit"
      @click="onAddFinance"
    />
    <template v-else>
      <el-row
        v-if="!loading"
        :gutter="20"
        class="d-flex flex-wrap"
      >
        <el-col :md="12">
          <add-new-card
            :disabled="!canEdit"
            class="mb-2 py-md-0"
            :title="$t('addFinancingOption')"
            horizontal
            min-height="62px"
            @click="onAddFinance"
          />
        </el-col>
        <el-col
          v-for="finance in filterFinances"
          :key="finance.id"
          class="d-flex mb-2"
          :md="12"
        >
          <set-up-and-projections-card
            class="w-100"
            :entity="finance"
            :name="finance.name"
            :date="$moment(finance.date).format('MMM YYYY')"
            :amount="getAmount(finance)"
            :type="finance.type"
            :disabled="!canEdit"
            view="list"
            @edit="onEditFinance"
            @copy="onCopyFinance"
            @delete="onDeleteFinance"
          />
        </el-col>
      </el-row>
    </template>

    <!-- Dialog Add Financing -->
    <dialog-add-financing
      :dialog-add-financing-visible="dialogAddFinancingVisible"
      :finance-action="financeAction"
      @close-dialog-add-financing="onCloseDialogAddFinancing"
      @close-dialog-on-button="onCloseDialogAddFinancing"
      @on-update-financing-view="onUpdateFinancing"
    />
    <!-- /Dialog Add Financing -->
  </div>
</template>

<script>
import EventBus from '@/event-bus'
import { mapGetters } from 'vuex'

import AddNewCard from '@/components/_v2/AddNewCard.vue'
import BusinessGuideSearch from '@/views/Home/Components/BusinessGuideSearch.vue'
import DialogAddFinancing from '@/views/Home/StoryMode/SetUp/Financing/Dialogs/DialogAddFinancing.vue'
import MixinApiCalculations from '@/mixins/apiCalculations'
import MixinCurrencies from '@/mixins/currencies'
import MixinDeleteIdeaExamples from '@/mixins/deleteIdeaExamples'
import MixinIdeaRoles from '@/mixins/permissions/ideaRoles'
import SetUpAndProjectionsCard from '@/views/Home/StoryMode/Components/Cards/SetUpAndProjectionsCard.vue'

export default {
  name: 'FinancingList',

  components: { BusinessGuideSearch, DialogAddFinancing, SetUpAndProjectionsCard, AddNewCard },

  mixins: [MixinApiCalculations, MixinCurrencies, MixinDeleteIdeaExamples, MixinIdeaRoles],

  data () {
    return {
      loading: false,
      searchValue: '',
      financeAction: null,
      dialogAddFinancingVisible: false
    }
  },

  computed: {
    ...mapGetters('idea', {
      finances: 'getFinances'
    }),

    isFinancesEmpty () {
      return !this.finances.length
    },

    filterFinances () {
      if (this.searchValue !== '' && this.searchValue) {
        return this.finances.filter(item => item.name.toUpperCase().includes(this.searchValue.toUpperCase()))
      }

      return this.finances
    }
  },

  mounted () {
    EventBus.$on('add-edit', () => {
      this.onAddFinance()
    })

    EventBus.$on('remove-examples', () => {
      this.deleteExamples()
    })
  },

  beforeDestroy () {
    EventBus.$off('add-edit')
    EventBus.$off('remove-examples')
  },

  methods: {
    onAddFinance () {
      if (!this.canEdit) return
      this.dialogAddFinancingVisible = true
    },

    onEditFinance (finance) {
      if (!this.canEdit) return
      this.financeAction = Object.assign({}, finance)
      this.dialogAddFinancingVisible = true
    },

    onCopyFinance (finance) {
      if (!this.canEdit) return
      this.financeAction = Object.assign({}, finance)
      this.financeAction.id = 0
      this.dialogAddFinancingVisible = true
    },

    onDeleteFinance (finance) {
      this.emitLoading(true)
      this.$store.commit('idea/removeFinance', finance.id)
      this.$http
        .delete(`story-mode/set-up/financing/finances/${finance.id}`)
        .then(async response => {
          await this.getFinancing()
          await this.getTotalCosts()
          await this.getProfitAndCashFlow()
          this.emitLoading(false)
        })
    },

    onCloseDialogAddFinancing () {
      this.dialogAddFinancingVisible = false
      this.financeAction = null
    },

    getAmount (finance) {
      if (finance.type === 'loc') {
        return this.getFormattedAmount(
          finance.howManyTimes * finance.averageWithdrawalAmount
        )
      }

      return this.getFormattedAmount(finance.amount)
    },

    async onUpdateFinancing () {
      this.emitLoading(true)
      await this.getFinancing()
      this.emitLoading(false)
    },

    onUpdateAddNewDialog () {
      this.emitLoading(true)
      Promise.all([
        this.getFinancing(),
        this.getTotalCosts(),
        this.getProfitAndCashFlow()
      ])
        .then(() => {
          this.emitLoading(false)
        })
        .finally(() => {
          this.emitLoading(false)
        })
    },

    deleteExamples () {
      this.deleteExamplesMixin('financing')
        .then(_ => { this.onUpdateAddNewDialog() })
    },

    emitLoading (value) {
      this.loading = value
      EventBus.$emit('loading', value)
      this.$emit('loading', value)
    }
  }
}
</script>
